import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useReveal } from "../hooks/useReveal";
import { useSEO } from "../hooks/useSEO";
import Countdown from "../components/Countdown";
import FollowBlock from "../components/FollowBlock";

/* ── Vue LISTE : grand bloc alterné (vue d'origine) ── */
function NomineeBlock({ nominee, index }) {
  const ref = useReveal();
  const flip = index % 2 === 1;
  return (
    <div
      ref={ref}
      className="reveal grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center py-20"
      data-testid={`nominee-block-${nominee.id}`}
    >
      <div className={flip ? "md:col-span-6 md:col-start-7 md:order-2" : "md:col-span-6"}>
        <div className="overflow-hidden" style={{ aspectRatio: "3 / 4", background: "#111", position: "relative" }}>
          <img src={nominee.portrait} alt={nominee.name} className="bw-image w-full h-full object-cover" loading="lazy" />
          {nominee.portrait_credit && (
            <span style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "5px 10px", background: "rgba(0,0,0,0.52)", fontFamily: "Inter, sans-serif", fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.38)", display: "block" }}>
              © {nominee.portrait_credit}
            </span>
          )}
        </div>
      </div>
      <div className={flip ? "md:col-span-5 md:col-start-1 md:row-start-1" : "md:col-span-5 md:col-start-8"}>
        <p className="label mb-4" style={{ color: "#A1A1AA" }}>
          {String(index + 1).padStart(2, "0")} / Nomination
        </p>
        <h3 className="font-serif" style={{ fontSize: "clamp(36px, 4.5vw, 64px)", lineHeight: 1, color: "#F8F6F0" }}>
          {nominee.name}
        </h3>
        <p className="font-serif italic mt-4" style={{ fontSize: 22, color: "#C9A961" }}>
          « {nominee.work} »
        </p>
        <p className="label mt-2">{nominee.work_type}</p>
        <p className="mt-8" style={{ color: "rgba(248,246,240,0.75)", fontSize: 16, lineHeight: 1.75 }}>
          {nominee.bio}
        </p>
        <div className="mt-8 flex gap-10">
          <Link to={`/artiste/${nominee.slug}`} className="link-editorial label" data-testid={`nominee-more-${nominee.id}`}>
            En savoir plus
          </Link>
          <Link
            to={`/voter?category=${nominee.category_slug}&nominee=${nominee.id}`}
            className="link-editorial label"
            style={{ color: "#C9A961", borderBottomColor: "#C9A961" }}
            data-testid={`nominee-vote-${nominee.id}`}
          >
            Voter
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ── Vue GRILLE : carte portrait compacte ── */
function NomineeCard({ nominee }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      data-testid={`nominee-card-${nominee.id}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Link to={`/artiste/${nominee.slug}`} style={{ textDecoration: "none" }}>
        <div className="overflow-hidden" style={{ aspectRatio: "3 / 4", background: "#111", position: "relative" }}>
          <img
            src={nominee.portrait}
            alt={nominee.name}
            className="bw-image w-full h-full object-cover"
            loading="lazy"
            style={{ transform: hovered ? "scale(1.04)" : "scale(1)", transition: "transform 900ms ease" }}
          />
          {nominee.portrait_credit && (
            <span style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "5px 10px", background: "rgba(0,0,0,0.52)", fontFamily: "Inter, sans-serif", fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.38)", display: "block" }}>
              © {nominee.portrait_credit}
            </span>
          )}
        </div>
        <div className="mt-5">
          <h3
            className="font-serif transition-colors duration-500"
            style={{ fontSize: "clamp(22px, 2.2vw, 30px)", lineHeight: 1.1, color: hovered ? "#C9A961" : "#F8F6F0" }}
          >
            {nominee.name}
          </h3>
          <p className="font-serif italic mt-1" style={{ fontSize: 16, color: "#C9A961" }}>
            « {nominee.work} »
          </p>
          <p className="label mt-2" style={{ color: "#A1A1AA" }}>{nominee.work_type}</p>
        </div>
      </Link>
      <Link
        to={`/voter?category=${nominee.category_slug}&nominee=${nominee.id}`}
        className="label inline-block mt-4"
        style={{ color: "#C9A961", borderBottom: "1px solid #C9A961", paddingBottom: 2 }}
      >
        Voter →
      </Link>
    </div>
  );
}

/* ── Icônes toggle ── */
const IconEditorial = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <rect x="0" y="1" width="7" height="9" fill="currentColor" />
    <rect x="9" y="1" width="9" height="2" fill="currentColor" />
    <rect x="9" y="5" width="9" height="2" fill="currentColor" />
    <rect x="9" y="9" width="9" height="2" fill="currentColor" />
    <rect x="0" y="14" width="18" height="2" fill="currentColor" />
  </svg>
);
const IconCompact = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <rect x="0" y="2" width="4" height="4" fill="currentColor" />
    <rect x="6" y="2" width="12" height="2" fill="currentColor" />
    <rect x="6" y="5" width="8" height="1.5" fill="currentColor" opacity="0.5" />
    <rect x="0" y="9" width="4" height="4" fill="currentColor" />
    <rect x="6" y="9" width="12" height="2" fill="currentColor" />
    <rect x="6" y="12" width="8" height="1.5" fill="currentColor" opacity="0.5" />
  </svg>
);
const IconGrid = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <rect x="0" y="0" width="8" height="8" fill="currentColor" />
    <rect x="10" y="0" width="8" height="8" fill="currentColor" />
    <rect x="0" y="10" width="8" height="8" fill="currentColor" />
    <rect x="10" y="10" width="8" height="8" fill="currentColor" />
  </svg>
);

function formatRevealDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}

export default function Nominations() {
  const [categories, setCategories] = useState([]);
  const [nomineesByCat, setNomineesByCat] = useState({});
  const [filterSlug, setFilterSlug] = useState("all");
  const [view, setView] = useState("editorial");
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);

  useSEO("Nominations", "Découvrez les nominé·e·s de la première édition du Stand d'Or. 13 catégories, sélection établie par le jury indépendant.");

  useEffect(() => {
    (async () => {
      const [catsRes, scheduleRes, nomRes] = await Promise.allSettled([
        api.get("/categories"),
        api.get("/reveal-schedule"),
        api.get("/nominees"),
      ]);
      const cats = catsRes.status === "fulfilled" ? catsRes.value.data : [];
      const grouped = {};
      if (nomRes.status === "fulfilled") {
        for (const n of nomRes.value.data) {
          if (!grouped[n.category_slug]) grouped[n.category_slug] = [];
          grouped[n.category_slug].push(n);
        }
      }
      // Batch all state updates in one go — prevents intermediate renders
      setCategories(cats);
      setNomineesByCat(grouped);
      if (scheduleRes.status === "fulfilled") setSchedule(scheduleRes.value.data);
      setLoading(false);
    })();
  }, []);

  // Attendre que toutes les données soient prêtes avant tout rendu
  if (loading) return null;

  // Avant la première révélation → écran d'attente
  const noneRevealed = schedule && categories.length === 0 && schedule.next_reveal;
  if (noneRevealed) {
    return (
      <div data-testid="nominations-page">
        <section
          className="relative overflow-hidden grain"
          style={{ minHeight: "100vh", background: "#000", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}
        >
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg,rgba(0,0,0,0.1) 0%,rgba(0,0,0,0.6) 60%,#000 100%)" }} aria-hidden="true" />
          <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pb-24 pt-[200px] w-full">
            <p className="label manifeste-fade" style={{ color: "#C9A961", marginBottom: 32 }}>
              Édition 2026 — Nominations officielles
            </p>
            <h1
              className="font-serif manifeste-title"
              style={{ fontSize: "clamp(56px, 10vw, 156px)", lineHeight: 0.92, letterSpacing: "-0.03em", color: "#F8F6F0", marginBottom: 48 }}
            >
              Les nominé·e·s
            </h1>
            <div className="hairline mb-16" style={{ maxWidth: 80, borderColor: "#C9A961" }} />
            <p className="label mb-12" style={{ color: "#A1A1AA" }}>
              Ouverture des nominations — {formatRevealDate(schedule.next_reveal.reveal_date)}
            </p>
            <Countdown targetIso={schedule.next_reveal.reveal_date} />
          </div>
        </section>
        <FollowBlock
          title="Les nominations arrivent."
          subtitle="Soyez parmi les premiers informés du lancement de la sélection officielle."
        />
      </div>
    );
  }

  const visibleCategories = filterSlug === "all"
    ? categories
    : categories.filter((c) => c.slug === filterSlug);

  const selectStyle = {
    background: "transparent",
    border: "1px solid #2a2a2a",
    color: "#F8F6F0",
    fontSize: 13,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    padding: "10px 36px 10px 14px",
    outline: "none",
    cursor: "pointer",
    appearance: "none",
    fontFamily: "Inter, sans-serif",
    fontWeight: 500,
    minWidth: 260,
  };

  return (
    <div data-testid="nominations-page">
      {/* Header */}
      <section className="pt-[160px] pb-16 max-w-[1400px] mx-auto px-6 lg:px-12">
        <p className="label mb-8" style={{ color: "#C9A961" }}>Édition 2026 — Nominations officielles</p>
        <h1
          className="font-serif"
          style={{ fontSize: "clamp(56px, 10vw, 156px)", lineHeight: 0.92, letterSpacing: "-0.03em", color: "#F8F6F0" }}
          data-testid="nominations-title"
        >
          Les nominé·e·s
        </h1>
        <p className="mt-10 max-w-2xl" style={{ color: "rgba(248,246,240,0.72)", fontSize: 18, lineHeight: 1.7 }}>
          Sélection établie par le jury à l'issue de dix-huit mois d'observation, de conseils de lecture, et de
          repérages en salles à travers la francophonie.
        </p>
      </section>

      {/* Ouverture des nominations */}
      {schedule?.next_reveal && (
        <div style={{ borderTop: "1px solid #1c1c1c", borderBottom: "1px solid #1c1c1c", background: "#050505" }}>
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-5">
              <p className="label mb-3" style={{ color: "#C9A961" }}>Prochaine révélation</p>
              <p className="font-serif" style={{ fontSize: "clamp(22px, 2.5vw, 36px)", color: "#F8F6F0", lineHeight: 1.1 }}>
                Toutes les nominations
              </p>
              <p className="label mt-2" style={{ color: "#A1A1AA" }}>{formatRevealDate(schedule.next_reveal.reveal_date)}</p>
            </div>
            <div className="md:col-span-6 md:col-start-7">
              <Countdown targetIso={schedule.next_reveal.reveal_date} />
            </div>
          </div>
        </div>
      )}

      {/* Barre de contrôle */}
      <div
        className="sticky z-30"
        style={{ top: 72, background: "rgba(10,10,10,0.94)", backdropFilter: "blur(12px)", borderTop: "1px solid #1c1c1c", borderBottom: "1px solid #1c1c1c" }}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex items-center justify-between gap-6" style={{ height: 56 }}>
          {/* Filtre catégorie */}
          <div style={{ position: "relative" }}>
            <select
              value={filterSlug}
              onChange={(e) => setFilterSlug(e.target.value)}
              style={selectStyle}
              aria-label="Filtrer par catégorie"
            >
              <option value="all" style={{ background: "#0A0A0A" }}>Toutes les catégories</option>
              {categories.map((c) => (
                <option key={c.slug} value={c.slug} style={{ background: "#0A0A0A" }}>
                  {String(c.order).padStart(2, "0")}. {c.title}
                </option>
              ))}
            </select>
            {/* Chevron */}
            <svg
              width="10" height="6" viewBox="0 0 10 6" fill="none"
              style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
            >
              <path d="M1 1L5 5L9 1" stroke="#A1A1AA" strokeWidth="1.5" strokeLinecap="square" />
            </svg>
          </div>

          {/* Toggle vue */}
          <div className="flex items-center gap-1">
            {[
              { key: "editorial", Icon: IconEditorial, label: "Vue éditorial" },
              { key: "liste", Icon: IconCompact, label: "Vue liste" },
              { key: "grille", Icon: IconGrid, label: "Vue grille" },
            ].map(({ key, Icon, label }) => (
              <button
                key={key}
                onClick={() => setView(key)}
                aria-label={label}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: "6px 10px",
                  color: view === key ? "#C9A961" : "#3a3a3a",
                  transition: "color 300ms",
                }}
              >
                <Icon />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Contenu */}
      {visibleCategories.map((cat) => (
        <section
          key={cat.slug}
          id={`cat-${cat.slug}`}
          className="max-w-[1400px] mx-auto px-6 lg:px-12"
          style={{ borderTop: "1px solid #1c1c1c" }}
          data-testid={`chapter-${cat.slug}`}
        >
          <div className="py-24 md:py-32">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-4">
              <div>
                <p className="label mb-6" style={{ color: "#A1A1AA" }}>
                  Chapitre&nbsp;{String(cat.order).padStart(2, "0")}
                </p>
                <h2
                  className="font-serif"
                  style={{ fontSize: "clamp(40px, 6vw, 96px)", lineHeight: 1, color: "#F8F6F0", letterSpacing: "-0.02em" }}
                >
                  {cat.title}
                </h2>
              </div>
              <Link
                to={`/voter?category=${cat.slug}`}
                className="label"
                style={{ color: "#C9A961", borderBottom: "1px solid #C9A961", paddingBottom: 4 }}
                data-testid={`chapter-vote-${cat.slug}`}
              >
                Voter dans cette catégorie →
              </Link>
            </div>
            <p className="max-w-3xl font-serif italic" style={{ color: "rgba(248,246,240,0.75)", fontSize: 22, lineHeight: 1.5 }}>
              {cat.long_description}
            </p>
          </div>

          {view === "editorial" ? (
            <div className="divide-y" style={{ borderTop: "1px solid #1c1c1c" }}>
              {(nomineesByCat[cat.slug] || []).map((n, i) => (
                <NomineeBlock key={n.id} nominee={n} index={i} />
              ))}
            </div>
          ) : view === "liste" ? (
            <ul style={{ borderTop: "1px solid #1c1c1c", marginBottom: 48 }}>
              {(nomineesByCat[cat.slug] || []).map((n, i) => (
                <li
                  key={n.id}
                  className="grid grid-cols-12 gap-6 items-center py-6 group"
                  style={{ borderBottom: "1px solid #1c1c1c" }}
                >
                  <span className="col-span-1 label" style={{ color: "#A1A1AA" }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="col-span-2 md:col-span-1" style={{ width: 48, aspectRatio: "3/4", background: "#111", flexShrink: 0 }}>
                    <img src={n.portrait} alt={n.name} className="bw-image w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="col-span-7 md:col-span-7">
                    <Link to={`/artiste/${n.slug}`} style={{ textDecoration: "none" }}>
                      <p
                        className="font-serif transition-colors duration-300 group-hover:text-[#C9A961]"
                        style={{ fontSize: "clamp(20px, 2vw, 28px)", color: "#F8F6F0", lineHeight: 1.1 }}
                      >
                        {n.name}
                      </p>
                    </Link>
                    <p className="font-serif italic mt-1" style={{ fontSize: 15, color: "#C9A961" }}>
                      « {n.work} »
                    </p>
                  </div>
                  <div className="col-span-2 md:col-span-3 flex justify-end">
                    <Link
                      to={`/voter?category=${n.category_slug}&nominee=${n.id}`}
                      className="label"
                      style={{ color: "#C9A961", borderBottom: "1px solid #C9A961", paddingBottom: 2 }}
                    >
                      Voter →
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-14 pb-24"
              style={{ borderTop: "1px solid #1c1c1c", paddingTop: 48 }}
            >
              {(nomineesByCat[cat.slug] || []).map((n) => (
                <NomineeCard key={n.id} nominee={n} />
              ))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}
